/* eslint-disable import-js/order */
import 'core-js/stable/iterator';
import './providers/zodConfig';

// need to import debug tools first
import './providers/whyDidYouRender';
import './providers/useWhatChanged';
// Init PostHog
import { posthog } from './providers/posthog';
import './providers/datadog';
import { createRoot } from 'react-dom/client';
import { BrowserRouter as Router } from 'react-router-dom';
import { PostHogProvider } from 'posthog-js/react';
import * as Sentry from '@sentry/react';
import { sentryOptions } from './providers/sentry';
import './UI/css/index.css';
import './UI/css/fonts.css';
import { log } from '@/logger/logger.ts';
import { App } from './App';
import { AuthProvider } from './contexts/AuthContext';
import { FigmaEmbedModeProvider } from './contexts/FigmaEmbedModeContext';
import { FigmaEmbedAiBalanceProvider } from './components/Recipe/FlowComponents/LeftPanelSections/FigmaEmbedAiBalanceMenu';
import { AxiosConfiguration } from './components/AxiosConfiguration/AxiosConfiguration';
import { CloudflareTurnstileClearanceHost } from './services/waf/cloudflare/CloudflareTurnstileClearanceHost';
import { WAFFailureModal } from './UI/WAFFailureModal/WAFFailureModal';
import { WAF_APP_CONTENT_ROOT_ID } from './UI/WAFInteractiveChallengeOverlay/WAFInteractiveChallengeOverlay';
import { initCloudflareWAFProvider } from './services/waf/cloudflare/initCloudflareWAFProvider';
import './language';
import { ErrorBoundary } from './components/ErrorBoundary/ErrorBoundary';
import { MobileCheck } from './components/MobileCheck/MobileCheck';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './providers/queryClient';
import initFavicon from './providers/favicon';
import initVersionBanner from './providers/versionBanner';
import initChunkReload from './providers/chunkReload';

Sentry.init(sentryOptions);
initFavicon();
initVersionBanner();
initChunkReload();
initCloudflareWAFProvider();

const logger = log.getLogger('Main');
logger.debug('App started.');

const root = createRoot(document.getElementById('root') as Element);
root.render(
  <ErrorBoundary>
    <Router
      future={{
        v7_relativeSplatPath: true,
        v7_startTransition: true,
      }}
    >
      <CloudflareTurnstileClearanceHost />
      {/* Mounted here, above every app provider, so a page-load challenge that terminally fails before <App>
          mounts (e.g. on the auth-restore request) still surfaces the failure modal. */}
      <WAFFailureModal />
      {/* Sibling target the interactive-challenge overlay marks `inert`; never `#root`, which contains the
          overlay itself. `display:contents` adds no box, so the app content lays out unchanged. */}
      <div id={WAF_APP_CONTENT_ROOT_ID} style={{ display: 'contents' }}>
        <PostHogProvider client={posthog}>
          <QueryClientProvider client={queryClient}>
            <AuthProvider>
              <FigmaEmbedModeProvider>
                <FigmaEmbedAiBalanceProvider>
                  <AxiosConfiguration>
                    <MobileCheck>
                      <App />
                    </MobileCheck>
                  </AxiosConfiguration>
                </FigmaEmbedAiBalanceProvider>
              </FigmaEmbedModeProvider>
            </AuthProvider>
          </QueryClientProvider>
        </PostHogProvider>
      </div>
    </Router>
  </ErrorBoundary>,
);
